import i18n from "@/utils/i18n";
import { apiClient } from "@/lib/apiClient";
import type {
  AuditLog,
  AuditLogListParams,
  AuditLogListResponse,
  AuditLogDetailResponse,
  AuditLogExportParams,
} from "../types/logs.types";

const AUDIT_LOGS_ENDPOINT = "/api/admin/audit-logs";

export function toAuditLogLabel(raw: string | null | undefined): string {
  if (!raw) return "—";
  const trimmed = String(raw).trim();
  if (!trimmed) return "—";
  const t = i18n.t.bind(i18n);
  const key = trimmed.toUpperCase().replace(/[\s_-]+/g, "_");
  const noUnderscore = trimmed.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const moduleKey = `logs.moduleLabels.${key}`;
  const moduleLabel = t(moduleKey);
  if (moduleLabel !== moduleKey) return moduleLabel;
  const entityKey = `logs.entityLabels.${noUnderscore}`;
  const entityLabel = t(entityKey);
  if (entityLabel !== entityKey) return entityLabel;
  return trimmed;
}

export function formatLogDateTime(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${day}.${month}.${year} / ${h}:${m}:${s}`;
}

export const logsApi = {
  /**
   * GET /api/admin/audit-logs - List audit logs with pagination, filtering, sorting.
   */
  getAll: async (
    params?: AuditLogListParams
  ): Promise<{ data: AuditLog[]; total: number }> => {
    const query: Record<string, unknown> = {};
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.module) query.module = params.module;
    if (params?.action) query.action = params.action;
    if (params?.status) query.status = params.status;
    if (params?.userID) query.userID = params.userID;
    if (params?.entityType) query.entityType = params.entityType;
    if (params?.startDate) query.startDate = params.startDate;
    if (params?.endDate) query.endDate = params.endDate;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    const { data } = await apiClient.get<AuditLogListResponse>(
      AUDIT_LOGS_ENDPOINT,
      { params: query }
    );
    return {
      data: data.data ?? [],
      total: data.count ?? 0,
    };
  },

  /**
   * GET /api/admin/audit-logs/{id} - Get single audit log by ID.
   */
  getById: async (id: string): Promise<AuditLog> => {
    const { data } = await apiClient.get<AuditLogDetailResponse>(
      `${AUDIT_LOGS_ENDPOINT}/${id}`
    );
    if (!data?.data) throw new Error("Log not found");
    return data.data;
  },

  /**
   * GET /api/admin/audit-logs/export - Export audit logs as CSV, Excel or PDF.
   * Returns blob; caller should trigger download (e.g. create object URL and link.click).
   */
  export: async (
    params: AuditLogExportParams
  ): Promise<{ blob: Blob; filename: string }> => {
    const query: Record<string, unknown> = { format: params.format };
    if (params.page != null) query.page = params.page;
    if (params.limit != null) query.limit = params.limit;
    if (params.search) query.search = params.search;
    if (params.module) query.module = params.module;
    if (params.action) query.action = params.action;
    if (params.status) query.status = params.status;
    if (params.userID) query.userID = params.userID;
    if (params.entityType) query.entityType = params.entityType;
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;

    const { data } = await apiClient.get<Blob>(
      `${AUDIT_LOGS_ENDPOINT}/export`,
      { params: query, responseType: "blob" }
    );
    const ext = params.format === "pdf" ? "pdf" : params.format === "excel" ? "xlsx" : "csv";
    const filename = `audit-logs-${new Date().toISOString().slice(0, 10)}.${ext}`;
    return { blob: data instanceof Blob ? data : new Blob([data]), filename };
  },
};
